import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { SignOutIcon } from '@/components/ui/icons'
import {
  useInstance,
  useLogout,
  useSetThemePreference,
  useMe,
  useRemoveAvatar,
  useUpdateDisplayName,
  useUploadAvatar,
} from '@/features/auth/api'
import type { ThemePreference } from '@/gql/graphql'
import { QualityFields } from '@/features/voice/QualityFields'
import { setPrefs } from '@/features/voice/prefs'
import { listMicrophones } from '@/features/voice/room'
import { useVoicePrefs } from '@/features/voice/useVoice'
import { errorMessage } from '@/lib/graphql'

import './settings.css'

export const Route = createFileRoute('/_app/settings/profile')({
  component: ProfileSettings,
})

function ProfileSettings() {
  const navigate = useNavigate()
  const logout = useLogout()
  const { data } = useMe()
  const updateName = useUpdateDisplayName()
  const uploadAvatar = useUploadAvatar()
  const instance = useInstance()
  const removeAvatar = useRemoveAvatar()

  const fileInput = useRef<HTMLInputElement>(null)
  const [displayName, setDisplayName] = useState(data?.me?.displayName ?? '')
  /** A local object URL, so the new face appears before the round trip. */
  const [preview, setPreview] = useState<string | null>(null)
  /** Set only by the local size check below; the server's refusals arrive as
   * `uploadAvatar.error` and share the same slot. */
  const [tooBig, setTooBig] = useState<string | null>(null)

  const me = data?.me
  const close = () => void navigate({ to: '/' })

  const pickFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // A courtesy, not the cap — the server refuses an oversized image regardless.
    // It saves uploading a 30-megapixel photograph in order to be told about it,
    // which on a slow connection is a minute of somebody's time.
    const maxBytes = instance.data?.instance.avatarMaxBytes
    if (maxBytes && file.size > maxBytes) {
      setTooBig(`Pictures can be at most ${Math.round(maxBytes / 1024 / 1024)} MB.`)
      event.target.value = ''
      return
    }
    setTooBig(null)

    // Shown immediately; replaced by the server's version once `me` refetches.
    setPreview(URL.createObjectURL(file))
    uploadAvatar.mutate(file, {
      onSettled: () => {
        setPreview((current) => {
          if (current) URL.revokeObjectURL(current)
          return null
        })
      },
    })
    // So picking the same file twice in a row still fires a change event.
    event.target.value = ''
  }

  if (!me) return null

  return (
    <Dialog open onClose={close} title="Your profile" wide>
      {/* A grid rather than `.dialog-body`'s single column: the sections flow
       * into as many columns as the dialog's width affords, which is what makes
       * that width worth having. See `.settings-grid`. */}
      <div className="settings-grid">
        <div className="settings-avatar">
          <Avatar displayName={me.displayName} avatarUrl={preview ?? me.avatarUrl} size={5} />

          <div className="settings-avatar-actions">
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={pickFile}
              className="sr-only"
              id="avatar-file"
            />
            <Button onClick={() => fileInput.current?.click()} disabled={uploadAvatar.isPending}>
              {uploadAvatar.isPending ? 'Uploading…' : 'Change picture'}
            </Button>
            {me.avatarUrl && (
              <Button
                variant="quiet"
                onClick={() => removeAvatar.mutate()}
                disabled={removeAvatar.isPending}
              >
                Remove
              </Button>
            )}
            <p className="field-hint">
              Square works best. It is scaled to 256&nbsp;px and stored with your account.
            </p>
          </div>
        </div>

        {(tooBig || uploadAvatar.isError) && (
          <p className="form-error" role="alert">
            {tooBig ??
              (uploadAvatar.error instanceof Error
                ? uploadAvatar.error.message
                : 'That image could not be uploaded.')}
          </p>
        )}

        {/*
          * Your name and your theme in one group, because they are the two things
          * here that follow the *account* — choose dark on a laptop and it is dark
          * on your phone. Everything below this point is a fact about the machine
          * you are sitting at, and that difference is the reason for the grouping.
          */}
        <section className="settings-section">
          <h3 className="settings-heading">You</h3>

          <div className="field">
            <label className="field-caps" htmlFor="profile-display-name">
              Display name
            </label>
            <input
              id="profile-display-name"
              className="field-input"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
            <p className="field-hint">
              Signed in as @{me.handle}. Handles cannot be changed.
            </p>
          </div>

          {updateName.isError && (
            <p className="form-error" role="alert">
              {errorMessage(updateName.error, 'That name could not be saved.')}
            </p>
          )}

          <ThemePicker current={me.themePreference} />
        </section>

        <VoiceAndSound />

        <PictureQuality />

        {/*
          * The instance panel had no entry point at all until now — it was
          * reachable only by typing the URL, which is not a door.
          *
          * Here rather than in the shell because it is the one surface that is
          * about *you* rather than about a room, and being the admin is a fact
          * about you.
          */}
        {me.isInstanceAdmin && (
          <div className="settings-section">
            <h3 className="settings-heading">This instance</h3>
            <Link to="/settings/instance" className="btn btn-secondary">
              Instance settings
            </Link>
            <p className="field-hint">
              Registration, the limits on uploads and picture quality, everyone&rsquo;s accounts,
              and the instance&rsquo;s own audit log.
            </p>
          </div>
        )}

        {/*
          * Signing out lives here now rather than in the shell: it is something
          * you do twice a year, and it was taking permanent residence beside
          * things you do hourly.
          */}
        <div className="settings-section">
          <h3 className="settings-heading">This device</h3>
          <button
            type="button"
            className="btn btn-quiet settings-signout"
            onClick={() =>
              logout.mutate(undefined, { onSuccess: () => void navigate({ to: '/login' }) })
            }
          >
            <SignOutIcon />
            Sign out
          </button>
        </div>
      </div>

      <div className="dialog-actions">
        <Button onClick={close}>Close</Button>
        <Button
          variant="primary"
          disabled={updateName.isPending || displayName === me.displayName}
          onClick={() => updateName.mutate(displayName)}
        >
          {updateName.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Dialog>
  )
}

/**
 * Voice preferences, which belong to this device rather than to the account.
 *
 * Which microphone, which speakers should make a noise, whether to hold a key
 * to talk — every one of them is a fact about the machine you are sitting at.
 * Syncing them would mean the mic you chose on your laptop being wrong on your
 * phone, so they live in localStorage and say so.
 *
 * **Picture quality belongs here for exactly that reason** — the connection you are
 * on today is a fact about where you are sitting, and a laptop tethered to a phone
 * should not inherit what you chose on fibre.
 *
 * It is *also* in the in-call popover now, and the same `QualityFields` renders
 * both. It was originally kept out of the call on the ground that a change only
 * landed at the next camera-on, which would have read as broken next to two controls
 * that apply instantly — so the fix was to make it apply instantly too, rather than
 * to hide it from the place people need it. See `room.applyCameraQuality`.
 *
 * These can only ever ask for *less* than the host allows — see `quality.ts` — so
 * a device preference is never a way around an instance limit.
 */
function VoiceAndSound() {
  const prefs = useVoicePrefs()
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([])

  useEffect(() => {
    void listMicrophones().then(setMicrophones)
  }, [])

  return (
    <section className="settings-section">
      <h3 className="settings-heading">
        Voice &amp; sound <span className="muted">— this device</span>
      </h3>

      <div className="field">
        <label className="field-caps" htmlFor="settings-mic">
          Microphone
        </label>
        <select
          id="settings-mic"
          className="field-input"
          value={prefs.microphoneId ?? ''}
          onChange={(event) => setPrefs({ microphoneId: event.target.value || null })}
        >
          <option value="">System default</option>
          {microphones.map((device) => (
            <option key={device.deviceId} value={device.deviceId}>
              {device.label || 'Microphone'}
            </option>
          ))}
        </select>
        {microphones.length === 0 && (
          <p className="field-hint">
            Device names appear once you have joined voice at least once — browsers hide them until
            you grant microphone access.
          </p>
        )}
      </div>

      <label className="settings-check">
        <input
          type="checkbox"
          checked={prefs.soundCues}
          onChange={(event) => setPrefs({ soundCues: event.target.checked })}
        />
        Play a soft cue when somebody joins or leaves voice
      </label>

      <label className="settings-check">
        <input
          type="checkbox"
          checked={prefs.pushToTalk}
          onChange={(event) => setPrefs({ pushToTalk: event.target.checked })}
        />
        Push to talk — hold <kbd>`</kbd> to speak
      </label>
      <p className="field-hint">
        Off by default: nook listens for your voice instead. Push to talk stands down while you are
        typing, and only works while this tab has focus — the web has no global hotkeys.
      </p>
    </section>
  )
}

/**
 * How much of your upload a picture costs, on this device.
 *
 * Its own section rather than the tail of Voice &amp; sound, and spanning the grid
 * rather than taking one column of it. Both follow from the same fact: this is two
 * settings (a camera, a screen) of two halves each (how big, how smooth), and that
 * shape is only visible if it has the width to lay itself out. Dealt into an 18rem
 * column it would be four selects in a stack, which is a list of controls rather
 * than a description of anything.
 */
function PictureQuality() {
  return (
    <section className="settings-section settings-span">
      <h3 className="settings-heading">
        Picture quality <span className="muted">— this device</span>
      </h3>
      <QualityFields idPrefix="settings" hintClassName="field-hint" />
    </section>
  )
}


const THEMES = [
  { id: 'SYSTEM', label: 'System' },
  { id: 'LIGHT', label: 'Light' },
  { id: 'DARK', label: 'Dark' },
] as const

/**
 * Light, dark, or whatever the operating system says.
 *
 * Server-side, so it follows you between devices — and `System` is a real
 * choice rather than the absence of one: picking it after having chosen dark is
 * an instruction, and it has to survive being given.
 */
function ThemePicker({ current }: { current: ThemePreference }) {
  const setTheme = useSetThemePreference()

  return (
    /* `.field` rather than `.settings-section`: this is one setting inside the
     * "You" group, and a section would draw its own separator across the middle of
     * that group as though a new one had started. */
    <div className="field">
      <p className="field-caps" id="theme-label">
        Theme
      </p>
      <div className="settings-segmented" role="radiogroup" aria-labelledby="theme-label">
        {THEMES.map((theme) => (
          <button
            key={theme.id}
            type="button"
            role="radio"
            aria-checked={current === theme.id}
            data-selected={current === theme.id || undefined}
            className="settings-segment"
            onClick={() => setTheme.mutate(theme.id)}
          >
            {theme.label}
          </button>
        ))}
      </div>
      <p className="field-hint">
        Dark mode is warm charcoal rather than blue-black &mdash; a room lit low, not a terminal.
      </p>
    </div>
  )
}
